import { isArray } from 'lodash'

import { DQLHTTPResponse } from './types'

/**
 * A utility function to assert that a response is a DQLHTTPResponse. This enforces
 * the structure of the response object, ensuring all of the *required* fields are present
 * and of the correct type.
 */
export const isDQLHTTPResponse = (
  response: unknown,
): response is DQLHTTPResponse => {
  return (
    typeof response === 'object' &&
    response !== null &&
    typeof (response as DQLHTTPResponse).queryType === 'string' &&
    typeof (response as DQLHTTPResponse).items === 'object' &&
    Array.isArray((response as DQLHTTPResponse).items) &&
    typeof (response as DQLHTTPResponse).error === 'object' &&
    typeof (response as DQLHTTPResponse).warnings === 'object' &&
    Array.isArray((response as DQLHTTPResponse).warnings)
  )
}

/**
 * A utility function to determine whether a DQL identifier should be wrapped in quotes. This will
 * primarily be used for various DQL query suggestions. Wrapping in quotes is required when:
 *
 * - Value does not start with [a-zA-Z_]
 * - Value contains non-alphanumeric characters [a-zA-Z0-9_] (excluding underscores)
 *
 * Empty strings will return false, and aren't really valid DQL identifiers anyway
 */
export const shouldWrapInTicks = (value: string): boolean => {
  const validStart = /[a-zA-Z_]/.test(value[0] ?? '')
  const validChars = /^[a-zA-Z0-9_]+$/.test(value)
  return !validStart || !validChars
}

/**
 * A utility function to extract the collection name from a DQL query. This handles various
 * DQL command types including:
 * - SELECT queries (e.g., "SELECT * FROM collection [WHERE|LIMIT|OFFSET]...")
 * - UPDATE queries (e.g., "UPDATE collection SET...")
 * - INSERT queries (e.g., "INSERT INTO collection [DOCUMENTS]...")
 *
 * The function validates the command structure around the collection name:
 * - For UPDATE: Must start with UPDATE and have SET after collection
 * - For SELECT: Must have FROM before collection, optionally followed by WHERE or LIMIT
 * - For INSERT: Must have INTO before collection, optionally followed by DOCUMENTS
 *
 * Collection names can be wrapped in backticks for names containing special characters
 * or spaces (e.g., "SELECT * FROM `my collection`").
 *
 * This function preserves the original casing of the collection name while performing
 * case-insensitive matching for the SQL commands.
 *
 * @param query - The DQL query string to parse
 * @returns The collection name if found and valid, or null if:
 *   - No valid command structure is found
 *   - The query contains 'from collection' (reserved for future use)
 */
export const findCollectionName = (query: string) => {
  const originalQuery = query
  const lowerQuery = query.toLowerCase()

  // TODO: (Tom) revisit this later once I have more knowledge of DQL. For now, just return null
  if (lowerQuery.includes('from collection')) return null

  // Helper function to extract name with original casing from a match
  const extractNameWithOriginalCasing = (
    match: RegExpMatchArray,
    groupIndex = 1,
  ) => {
    if (!match || match.index === undefined) return null

    // Calculate the exact position of the collection name in the original string:
    // 1. match.index gives us the start position of the entire match in the lowercase string
    // 2. match[0].indexOf(match[groupIndex]) gives us the offset of the group (collection name) within the match
    // 3. Using these together, we find the exact character position where the collection name starts
    const matchStart = match.index + match[0].indexOf(match[groupIndex])
    const matchLength = match[groupIndex].length

    // Extract the collection name with its original casing from the original query string
    const name = originalQuery.substring(matchStart, matchStart + matchLength)

    // Handle backtick-wrapped names
    if (name.startsWith('`')) {
      const fullNameMatch = originalQuery.substring(matchStart).match(/`[^`]+`/)
      const fullName = fullNameMatch?.[0]
      return fullName || name
    }

    return name
  }

  // First check for UPDATE queries since they need special handling
  const updateMatch = lowerQuery.match(/^\s*update\s+([^\s]+)\s+set\b/)
  if (updateMatch) return extractNameWithOriginalCasing(updateMatch)

  // Handle SELECT and INSERT queries
  const selectMatch = lowerQuery.match(
    /\bfrom\s+([^\s]+)(?:\s+(?:where|limit|offset)\b|\s*$)/,
  )
  const insertMatch = lowerQuery.match(
    /\binto\s+([^\s]+)(?:\s+documents\b|\s*$)/,
  )

  const match = selectMatch || insertMatch
  if (match) return extractNameWithOriginalCasing(match)

  return null
}

/**
 * Validates if a string is a non-empty JSON object or array of objects
 * @param jsonToValidate The JSON string to validate
 * @returns true if the string is valid JSON containing either:
 * - A non-empty object with at least one property
 * - A non-empty array of objects, where each object has at least one property
 * @returns false for empty objects, empty arrays, primitives, or invalid JSON
 */
export const validateJsonObjectContent = (jsonToValidate: string): boolean => {
  jsonToValidate = jsonToValidate.replace(/\n/g, '').trim()

  try {
    const parsed = JSON.parse(jsonToValidate)

    // Handle case where the parsed result is a string that might contain JSON
    if (typeof parsed === 'string') {
      const trimmed = parsed.trim()
      if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
      ) {
        try {
          return validateJsonObjectContent(trimmed)
        } catch {
          return false
        }
      }

      return false
    }

    if (parsed === null || parsed === undefined) return false

    // Check a normal JSON object has keys
    if (typeof parsed === 'object' && !isArray(parsed)) {
      return Object.keys(parsed).length > 0
    }

    // Handle arrays of objects
    if (isArray(parsed)) {
      if (parsed.length === 0) return false

      if (parsed.every((item) => typeof item === 'object' && item !== null)) {
        return parsed.every((item) => Object.keys(item).length > 0)
      }
    }

    // Primitives (string, number, boolean)
    return false
  } catch {
    return false
  }
}

/**
 * Generates a SQL query for selecting all records from a collection with an optional limit.
 * Handles wrapping collection names in backticks when needed (for names with special characters).
 *
 * @param collection - The name of the collection to query
 * @param limit - Optional limit on the number of records to return
 * @returns A properly formatted SQL query string
 */
export const buildQueryForEntireCollection = (
  collection: string,
  limit?: number,
): string => {
  const hasLimit = limit && limit > 0
  const limitClause = hasLimit ? ` LIMIT ${limit}` : ''

  if (shouldWrapInTicks(collection)) {
    return `SELECT * FROM \`${collection}\`${limitClause}`
  } else {
    return `SELECT * FROM ${collection}${limitClause}`
  }
}
